<template>
  <section class="client-picker" aria-label="Klient">
    <div v-if="!selectedClient && !editing" class="picker-modes">
      <button v-if="canSearch" type="button" :aria-pressed="String(mode === 'search')" :disabled="disabled || savingClient" @click="mode = 'search'">Nájsť klienta</button>
      <button v-if="canCreate" type="button" :aria-pressed="String(mode === 'create')" :disabled="disabled || savingClient" @click="mode = 'create'">Vytvoriť klienta</button>
    </div>
    <client-details-form v-if="editing || (!selectedClient && mode === 'create')"
      :client="editing ? selectedClient : undefined" :broker-id="brokerId"
      @saved="clientSaved" @cancel="cancelForm" @busy="savingClient = $event" />
    <div v-else-if="selectedClient" class="picked-client">
      <strong>{{ selectedClient.first_name }} {{ selectedClient.last_name }}</strong>
      <span v-if="visibleEmail(selectedClient)">{{ visibleEmail(selectedClient) }}</span>
      <span v-if="selectedClient.client_profile">{{ selectedClient.client_profile.telephone }}</span>
      <div class="picker-actions">
        <button type="button" :disabled="disabled" @click="editing = true">Upraviť údaje</button>
        <button type="button" :disabled="disabled" @click="clearClient">Zmeniť klienta</button>
      </div>
    </div>
    <div v-else-if="canSearch" class="picker-search">
      <div class="picker-search-field">
      <label :for="searchId" class="field-label">Meno, email alebo telefón</label>
      <input :id="searchId" v-model="query" type="search" class="text-input w-input"
        placeholder="Začnite písať meno alebo kontakt" :disabled="disabled" @input="queueSearch" @keydown.enter.prevent="search" />
      </div>
      <p v-if="searching" role="status">Vyhľadávam…</p>
      <p v-else-if="searched && !clients.length && !error" role="status">Nenašli sme žiadneho klienta.{{ canCreate ? ' Môžete vytvoriť nového.' : '' }}</p>
      <div v-for="client in clients" :key="client.id" class="search-result">
        <div><strong>{{ client.first_name }} {{ client.last_name }}</strong><small>{{ visibleEmail(client) }} {{ client.client_profile && client.client_profile.telephone }}</small></div>
        <button type="button" :disabled="disabled || searching" @click="selectClient(client)">Vybrať</button>
      </div>
    </div>
    <p v-if="error" class="clientError" role="alert">{{ error }}</p>
  </section>
</template>

<script>
import { getClientById, getClients } from '@/services/brokerService';
import ClientDetailsForm from './ClientDetailsForm.vue';

export default {
  name: 'ClientPicker',
  components: { ClientDetailsForm },
  props: {
    clientInit: Number,
    formOnly: Boolean,
    searchOnly: Boolean,
    complex: Boolean,
    zalozit: Boolean,
    disabled: Boolean,
    brokerId: Number,
  },
  data() {
    return { selectedClient: null, editing: false, mode: this.formOnly ? 'create' : 'search',
      query: '', clients: [], searching: false, searched: false, error: '', timer: null,
      requestId: 0, savingClient: false, searchId: `client-search-${this._uid}` };
  },
  computed: {
    busy() { return this.savingClient || this.editing || (!this.selectedClient && this.mode === 'create'); },
    canCreate() { return this.complex || this.formOnly || !this.searchOnly; },
    canSearch() { return !this.formOnly; },
  },
  watch: {
    busy: { immediate: true, handler(value) { this.$emit('busy', value); } },
    clientInit: { immediate: true, async handler(id) {
      if (!id) { this.selectedClient = null; return; }
      try {
        const client = await getClientById(id, 'client_profile');
        if (this.clientInit === id) this.selectedClient = client;
      } catch (error) { this.error = 'Údaje klienta sa nepodarilo načítať.'; }
    } },
  },
  beforeDestroy() { clearTimeout(this.timer); this.requestId++; },
  methods: {
    visibleEmail(client) { return client.email?.includes('@nomail.bankari.sk') ? '' : client.email || ''; },
    queueSearch() {
      clearTimeout(this.timer);
      this.requestId++;
      this.clients = [];
      this.searched = false;
      this.searching = false;
      this.error = '';
      this.timer = setTimeout(this.search, 300);
    },
    async search() {
      clearTimeout(this.timer);
      const requestId = ++this.requestId;
      const query = this.query.trim();
      this.error = '';
      if (!query) { this.clients = []; this.searched = false; this.searching = false; return; }
      this.searching = true;
      try {
        const result = await getClients({ search: query, page_size: 10, expand: 'client_profile', ...(this.brokerId ? { financnik: this.brokerId } : {}) });
        if (requestId !== this.requestId) return;
        this.clients = result.results;
        this.searched = true;
      } catch (error) {
        if (requestId === this.requestId) this.error = 'Vyhľadávanie zlyhalo. Skúste to znova.';
      } finally { if (requestId === this.requestId) this.searching = false; }
    },
    selectClient(client) { this.selectedClient = client; this.$emit('client-picked', client); },
    clientSaved(client) {
      this.editing = false;
      this.selectClient(client);
      this.$emit('client-created', client);
    },
    cancelForm() { this.editing = false; this.mode = this.formOnly ? 'create' : 'search'; },
    clearClient() { this.selectedClient = null; this.mode = this.formOnly ? 'create' : 'search'; this.$emit('client-picked', null); },
  },
};
</script>

<style scoped>
.client-picker { margin-bottom: 20px; color: var(--app-text, #0a1953); min-width: 0; }
.picker-modes { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; padding: 5px; margin-bottom: 16px; border: 1px solid var(--app-border, #dce5f0); border-radius: 16px; background: var(--app-surface, #fff); }
.picker-modes button { min-height: 44px; padding: 10px 8px; border: 1px solid transparent; border-radius: 11px; color: var(--app-text, #0a1953); font-size: 13px; font-weight: 700; line-height: 1.4; background: transparent; cursor: pointer; }
.picker-modes button[aria-pressed="true"] { background: #286cb2; color: #fff; box-shadow: 0 2px 6px rgba(0,0,0,.12); }
.picker-modes button:hover:not(:disabled):not([aria-pressed="true"]) { background: var(--app-primary-soft, #e8f2fc); }
.picker-search-field { position: relative; }
.picker-search-field .field-label { left: 20px; top: 12px; margin: 0; color: var(--app-text-muted, #6f7f96); font-size: 12px; }
.picker-search-field .text-input { width: 100%; min-height: 72px; padding: 32px 20px 13px; margin: 0; border: 1px solid var(--app-border, #dce5f0); border-radius: 18px; font-size: 15px; line-height: 1.5; box-sizing: border-box; }
.picker-search p { margin: 12px 0 0; color: var(--app-text-muted, #6f7f96); font-size: 13px; line-height: 1.6; }
.picker-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.picker-actions button, .search-result button { border: 1px solid var(--app-border, #dce5f0); border-radius: 10px; min-height: 40px; padding: 9px 13px; background: var(--app-primary-soft, #e8f2fc); color: var(--app-primary, #286cb2); font-size: 13px; font-weight: 700; cursor: pointer; }
.picked-client, .search-result { padding: 18px; border: 1px solid var(--app-border, #dce5f0); border-radius: 18px; margin: 10px 0; background: var(--app-surface, #fff); }
.picked-client { display: flex; flex-direction: column; gap: 6px; overflow-wrap: anywhere; }
.picked-client > span, .search-result small { color: var(--app-text-muted, #6f7f96); font-size: 12px; line-height: 1.6; }
.search-result { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.search-result > div { min-width: 0; overflow-wrap: anywhere; }
.search-result small { display: block; }
.search-result button { flex-shrink: 0; }
button:focus-visible, .picker-search-field input:focus-visible { outline: 2px solid var(--app-primary, #286cb2); outline-offset: 3px; }
button:disabled { opacity: .55; cursor: default; }
</style>
